import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Gallery CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore Our Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function GalleryOverview() {
  const ctaLink = useSlotGroup('galleryoverview.cta_link', ctaLinkDef);

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-8 md:pt-12 pb-8 md:pb-12 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl px-4 sm:px-6 lg:px-8 text-left">
        <div ref={headingRef} className="reveal-fade-up">
          <RichTextContent
            path="galleryoverview.pre_title"
            as="p"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-primary)] mb-6 tracking-wide uppercase"
          />

          <RichTextContent
            path="galleryoverview.title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--vibe-color-text)] mb-4 leading-tight uppercase"
          />

          <div className="w-20 h-1 mb-8 rounded-full bg-[var(--vibe-color-primary)]" />

          <RichTextContent
            path="galleryoverview.description"
            as="div"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl leading-relaxed [&_a]:text-[var(--vibe-color-primary)] [&_a]:underline"
          />

          <EditableSlotGroup groupPath="galleryoverview.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/gallery'}
              target={ctaLink._target || '_self'}
              className="inline-block text-center mt-8 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase rounded-full border border-[var(--vibe-color-primary)] text-[var(--vibe-color-text)] hover:bg-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] transition-all"
            >
              {ctaLink.label || 'Explore Our Gallery'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryOverview',
  displayName: 'Gallery Overview',
  tokens: {
    'galleryoverview.pre_title': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: 'Local Charm',
    },
    'galleryoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Everything You Need At Your Doorstep',
    },
    'galleryoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_home_gallery_blurb',
      defaultValue: '<p>Take a closer look at life in our community. Browse our gallery to explore the spaces, finishes, and surroundings that make this the perfect place to call home.</p>',
    },
  },
  slotGroups: {
    'galleryoverview.cta_link': ctaLinkDef,
  },
};

(GalleryOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryOverview;
